import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { roundNumberAndDecimals } from '@teinor/erp/prices'
import { totalCalculateParams } from '@teinor/erp/prices/quote/calculate'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import DisplayPrice from '../../../../../../baseComponents/Displays/DisplayPrice'
import { ModalFullCentered } from '../../../../../../baseComponents/Modal'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import NullComponent from '../../../../../../baseComponents/NullComponent'
import InputTable from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable'
import { ITDragHandle, ITSortableItem } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Sort'
import { ColumnITLayoutTableConfiguration, ITHookFunctionTy, ITLayoutTableOnChangeInputs } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/types'
import { TableWithoutDefaultRowRenderer } from '../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { TableWithoutVirtualListingContainerRenderer, TableWithoutVirtualListingRowProps, TableWithoutVirtualListingRowRenderer } from '../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing/types'
import { DeleteRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import { twinFetchPostJSON, twinFetchPostText } from '../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { DictionaryComponents } from '../../../../../../utils/globals/types'
import useIsLoading from '../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { getISOCurrencyMain } from '../../../../../../utils/reducers/getters'
import FooterInputTableModalSelect, { FooterLeftInputTableModalComponentProps } from '../../../../../inputTableLayouts/FooterInputTableModalSelect'
import { OnChangeOrderAddProduct } from '../../../../../inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/Inputs/OrderSearchProductInput/types'
import { useOrderLayoutTableLogic } from '../../../../../inputTableLayouts/order/OrderLayout/OrderTable/useOrderLayoutTableLogic'
import { OrderLayoutTableFilter } from '../../../../../inputTableLayouts/order/OrderLayout/OrderTable/useOrderLayoutTableLogic/types'
import { ColumnOrderTableSchema, OrderData } from '../../../../../inputTableLayouts/order/OrderLayout/types'
import { ProductEscandallingWithLang } from '../types'
import { DisplayEditCostTotalProductEscandalling, DisplayEditPercentageTotalProductEscandalling, ITDisplayEditFinalPriceProdEscandalling, ProductEscandallingInputPercentage, ProductEscandallingInputPrice, ProductEscandallingInputQuantity, ProductEscandallingSearchProductInputName } from './components'
import { filterCompleteDataByFiltersPE, filterTableDataProductEscandalling, parseBeforeSubmit, parseDataToProductEscandallingTD } from './functions'
import { ProductEscandallingTDTy } from './types'
import { ITDisplayPrice } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import ModalChangeSpecificPriceRules from '../../../Subcomponents/ModalChangeSpecificPriceRules'
import useEditDeleteModal from '../../../../../../utils/hooks/useEditDeleteModal'

interface ModalManageProductEscandallingProps extends ModalEditComponentProps { }

const ModalManageProductEscandalling: React.FC<ModalManageProductEscandallingProps> = ({ basicData, setOpened, onSubmit, ...rest}) => {
    const {t} = useTwinTranslation()
    const currencyMain = useMemo(() => getISOCurrencyMain(), [])

    const { data, setData, rowRenderer, loading, saveData, memoTableData, openModal, setOpenModal } = useModalManageProductEscandallingLogic({ setOpened, onSubmit, basicData, ...rest })
    const columns: ColumnOrderTableSchema<ProductEscandallingWithLang> = [
        {
            id: 'name',
            label: t('name', 'Nombre'),
            dataKey: 'name',
            component: 'ProductEscandallingSearchProductInputName',
            field: 'referenceAndName',
            typeOnChange: 'onChangeAdd',
            selectedIds: data.selectedIds,
            displayField: 'name'
        },
        {
            id: 'cost',
            dataKey: 'cost',
            label: t('cost', 'Coste'),
            component: 'ITInputPrice',
            typeOnChange: 'onChange'
        },
        {
            id: 'scaleQuantity',
            dataKey: 'scaleQuantity',
            label: t('quantity', 'Cantidad'),
            component: 'ITInputQuantity',
            typeOnChange: 'onChange'
        },
        {
            id: 'rowPriceCost',
            dataKey: 'rowPriceCost',
            label: t('rowPriceCostShort', 'Coste línea'),
            simpleLabel: t('rowPriceCost', 'Coste por línea'),
            component: 'ITDisplayPrice',
            width: 100
        },
        {
            id: 'margin',
            dataKey: 'margin',
            label: t('margin', 'Margen'),
            component: 'ITInputPercentage',
            typeOnChange: 'onChange'
        },
        {
            id: 'rowPriceCostWithMargin',
            dataKey: 'rowPriceCostWithMargin',
            label: t('costPriceWithMarginShort', 'PCM'),
            simpleLabel: t('rowCostPriceWithMargin', 'Precio coste línea con margen'),
            toolTip: t('rowCostPriceWithMargin', 'Precio coste línea con margen'),
            component: 'ITDisplayEditFinalPrice',
            width: 100
        },
        {
            id: 'unitPriceCostWithMargin',
            dataKey: 'unitPriceCostWithMargin',
            label: t('unitCostPriceWithMarginShort', 'U. PCM'),
            simpleLabel: t('unitCostPriceWithMargin', 'Precio coste unitario con margen'),
            toolTip: t('unitCostPriceWithMargin', 'Precio coste unitario con margen'),
            component: 'ITDisplayEditFinalPrice',
            width: 100
        },
        {
            id: 'dragLine',
            dataKey: 'order',
            label: '',
            simpleLabel: t('moveLine', 'Mover línea'),
            width: 33,
            component: 'ITDragHandle',
            requiredColumn: true
        },
    ]
    let totalCost = 0
    let totalCostMargin = 0
    for (const element of data.tableData) {
        if(element.rowPriceCost){
            totalCost += parseFloat(element.rowPriceCost)
        }
        if(element.rowPriceCostWithMargin){
            totalCostMargin += parseFloat(element.rowPriceCostWithMargin)
        }
    }
    const marginCalculated = totalCostMargin && totalCost ? roundNumberAndDecimals(((totalCostMargin * 100) / totalCost) - 100, totalCalculateParams) : 0
    const footerLeftComponents: FooterLeftInputTableModalComponentProps[] = [
        {
            label: t('costProduct', 'Coste producto'),
            children: <DisplayPrice value={totalCost} CurrencyId={getISOCurrencyMain()} with_vat={false} calculateParams={{
                decimals: 2,
                rounding: '0.01',
                roundingPriceType: 'medium'
            }} className='medium14' />
        },
        {
            label: t('costProductMargin', 'Coste producto margen'),
            children: <DisplayEditCostTotalProductEscandalling {...{totalCostMargin, marginCalculated, totalCost, setData}} />
        },
        {
            label: t('calculatedMargin', 'Margen calculado'),
            children: <DisplayEditPercentageTotalProductEscandalling {...{ totalCostMargin, marginCalculated, totalCost, setData }}  />
        },
    ]
    return (
        <ModalFullCentered opened={true} setOpened={setOpened} onClickOut={false}>
            <h2 className='mb-20'><TwinTrans transKey='manageEscandalling'>Gestionar Escandallos</TwinTrans>: {basicData?.reference + ' - ' + basicData?.name}  </h2>
            <InputTable modelId='SellOrderId' TableChildren={NullComponent} userPermissions={{ n: 1, permission: 'read' }} completeData={{ header: {}, originalTableData: [], ...data, tableData: memoTableData.tableData, dictPosTableData: memoTableData.dictPosTable }} CurrencyId={currencyMain} columns={columns} setCompleteData={setData as any} getCompleteData={async () => { }} inputTableURLs={{} as any} orderHeaderURLs={{} as any} setTableConfiguration={() => { }} tableConfiguration={{} as any} name='modalManageProductEscandalling' hookFunction={useManageProductEscandallingHook} orderStatusDict={{} as any} components={productEscandallingLayoutComponents} rowRenderer={rowRenderer} />
            <FooterInputTableModalSelect setOpened={() => setOpened(null)} textAccept={t('save', 'Guardar')} textCancel={t('cancel', 'Cancelar')} onAccept={saveData} disabled={data?.tableData?.length ? false : true} footerLeftComponents={footerLeftComponents} loading={loading} />
            {openModal?.type === 'edit' ? <ModalChangeSpecificPriceRules setOpened={() => setOpenModal(null)} onSubmit={() => setOpened(null)} allRowData={openModal.allRowData} /> : null}
        </ ModalFullCentered>
    )
}

interface ModalManageProductEscandallingLogicProps extends ModalEditComponentProps { }

const useModalManageProductEscandallingLogic = ({ onSubmit, setOpened, basicData }: ModalManageProductEscandallingLogicProps) => {
    const [data, setData] = useState<ProductEscandallingTDTy>({tableData: [], selectedProducts: {}, dictPosTableData: {}, filters: { generalSearchbar: '', order: {}, singleFieldsSearch: {} }, selectedIds: []})
    const memoTableData = useMemo(() => filterTableDataProductEscandalling(data), [data])

    const { startLoading, endLoading, loading } = useIsLoading()
    const { openModal, setOpenModal } = useEditDeleteModal()

    const addButtonRowRender = useCallback((props: TableWithoutVirtualListingRowProps, addNewProductLine: () => void) => {
        if (!props.style) {
            props.style = {}
        }
        return (
            <div {...props}>
                <div className=' table_row flex items-center justify-center w-full bg-gray-EF add_product_row cursor-pointer input_table_header_row' onClick={addNewProductLine}>
                    <span>+<span className='ml-5'><TwinTrans transKey='addLine'>Añadir fila</TwinTrans></span></span>
                </div>
            </div>
        )
    }, [])
    
    const addNewProductLine = useCallback(() => {
        setData((old) => {
            if (!old) {
                return null
            } else {
                const newOld = JSON.parse(JSON.stringify(old))
                if (newOld.tableData.length) {
                    let id: number
                    if (newOld.tableData.length === 1) {
                        id = 1
                    } else {
                        id = newOld.tableData[(newOld.tableData.length - 2)].id + 1
                    }
                    newOld.tableData.splice(newOld.tableData.length - 1, 0, { id })
                    return { ...newOld }
                }
                return { ...newOld }
            }
        })
    }, [setData])

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/product/productEscandalling/getAllProductsEscandallingComplete', {
            where: {
                'ProductEscandallingRel.ProductId': basicData?.id,
            },
            order: [['name', 'asc']]
        })
        if (result) {
            setData(parseDataToProductEscandallingTD(result))
        }
    }, [setData, basicData?.id])


    const rowRenderer: TableWithoutVirtualListingRowRenderer = useCallback((props) => {
        if (props?.rowData?.type === 'addButton') {
            props.key = 'addButton'
            return addButtonRowRender(props, addNewProductLine)
        }
        let extraClassName = ''
        if (props.rowIndex === 0) {
            extraClassName += 'modal_prepare_order_first_row'
        }
        return <TableWithoutDefaultRowRenderer {...props} className={(props.className || '') + ' ' + extraClassName} />
    }, [addNewProductLine, addButtonRowRender])


    const saveData = useCallback(async () => {
        startLoading()
        const extraProps = parseBeforeSubmit(data.tableData, basicData?.id)
        const result = await twinFetchPostText('/api/app/product/productEscandalling/bulkUpdateProductsEscandalling', parseBeforeSubmit(data.tableData, basicData?.id))
        if(result){
            onSubmit?.(result, {})
            setOpenModal({ type: 'edit', allRowData: { margin: extraProps.marginCalculated, costPrice: extraProps.totalCost, calculatedCost: extraProps.totalCostMargin, id: extraProps.ProductId } })
        }
        endLoading()
    }, [data.tableData, startLoading, endLoading, basicData?.id, onSubmit, setOpenModal])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, setData, rowRenderer, memoTableData, loading, saveData, openModal, setOpenModal }
}

const useManageProductEscandallingHook: ITHookFunctionTy = ({ setCompleteData, ...props}) => {
    const { eventFunctions, addCustomRender, ...rest } = useOrderLayoutTableLogic({ setCompleteData, ...props })
    const { t } = useTwinTranslation()
    const [activeNChildren, setActiveNChildren] = useState<number | null>(null);

    const deleteMsg = t('delete', 'Eliminar')
    
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 50,
                tolerance: 50
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const handleOnDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event
        if (over && active.id !== over.id) {
            let orderProducts: TwinDictionary[] = []
            flushSync(async () => {
                await setCompleteData((old) => {
                    if (!old) {
                        return null
                    }
                    let copy = JSON.parse(JSON.stringify(old))
                    const items = copy.tableData
                    const activeIndex = active.data?.current?.myIndex
                    const overIndex = over.data?.current?.myIndex
                    const myMoveItems = items.slice(activeIndex, activeIndex + (activeNChildren || 0) + 1)
                    items.splice(activeIndex, myMoveItems.length)
                    let myOverIndex = overIndex
                    if (overIndex > activeIndex) {
                        if (over.data?.current?.lastPosition) {
                            myOverIndex = over.data?.current?.lastPosition - myMoveItems.length + 1
                        } else {
                            myOverIndex = overIndex - myMoveItems.length + 1
                        }
                    }
                    items.splice(myOverIndex, 0, ...myMoveItems)
                    let order = 1
                    let inventPosition = 0
                    copy.dictPosTableData = {}
                    for (const key in items) {
                        const element = items[key]
                        element.absoluteIndex = inventPosition
                        if (element.ProductId && element.id) {
                            if (element.order !== order) {
                                element.order = order
                                orderProducts.push({ order: element.order, id: element.id, OrderId: element.OrderId, ProductId: element.ProductId, taxUsed: element.taxUsed })
                            }
                            order++
                        }
                        element.inventPosition = inventPosition
                        copy.dictPosTableData[element.id] = inventPosition
                        inventPosition++
                    }
                    copy.tableData = items
                    return copy
                })
                setActiveNChildren(null)
            })
        }
    }, [setCompleteData, activeNChildren])

    const containerRenderer: TableWithoutVirtualListingContainerRenderer = useCallback((props) => {
        const result: JSX.Element[] = []
        const resultIds: any[] = []
        for (const key in props.children) {
            const element = props.children[key]
            const dataElement = element?.props?.rowData
            let top = element.props.style.top
            element.props.style.top = undefined
            element.props.style.position = undefined
            result.push(<ITSortableItem id={dataElement?.id} key={dataElement?.id} data={{ nChildren: 0, myIndex: dataElement?.inventPosition }} className='flex-col' style={{ ...element?.props?.style || {}, top, position: 'absolute' }}>{element}</ITSortableItem>)
            resultIds.push({ id: dataElement?.id })
        }
        return (
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                onDragEnd={handleOnDragEnd} onDragStart={({ active }) => {
                    setActiveNChildren(active?.data?.current?.nChildren)
                }} onDragCancel={() => {
                    setActiveNChildren(null);
                }}>
                <SortableContext strategy={verticalListSortingStrategy} items={resultIds}>
                    <div className='data_rows h-full' style={props.style}>{result}</div>
                </SortableContext>
            </DndContext>
        )
    }, [sensors, handleOnDragEnd, setActiveNChildren])
    
    const parseTableDataProductEscandalling = useCallback((createFilters: (oldFilters: OrderLayoutTableFilter) => OrderLayoutTableFilter, completeData?: OrderData) => {
        setCompleteData((old) => {
            if (!old && !completeData) {
                return null
            }
            let myTableData = old
            const filters = myTableData?.filters ? createFilters(myTableData?.filters) : { generalSearchbar: '', order: {}, singleFieldsSearch: {} }
            if (completeData) {
                myTableData = completeData
            }
            if (!myTableData) {
                return null
            }
            let myfilteredTableData: any = []
            if (filters.generalSearchbar !== '' || Object.keys(filters.singleFieldsSearch)?.length) {
                myfilteredTableData = filterCompleteDataByFiltersPE(myTableData?.tableData || [], filters)
                let processedTableLength = 0
                return { ...myTableData, filteredTableData: myfilteredTableData, processedTableLength, filters } as any
            }
            let processedTableLength = 0
            return { ...myTableData, filteredTableData: null, processedTableLength, filters }
        })
    }, [setCompleteData])

    const onChangeSearchBar = useCallback((value: string) => {
        parseTableDataProductEscandalling((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, generalSearchbar: value }
        })
    }, [parseTableDataProductEscandalling])

    const changeSingleField = useCallback((fieldName: string, value: string) => {
        parseTableDataProductEscandalling((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            let myFilters = {
                ...copy,
                singleFieldsSearch: {
                    ...copy.singleFieldsSearch,
                    [fieldName]: value
                }
            }
            if (value === '') {
                delete myFilters.singleFieldsSearch[fieldName]
            }
            return myFilters
        })
    }, [parseTableDataProductEscandalling])

    const myOnChangeAdd: OnChangeOrderAddProduct = useCallback(async (product) => {
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            const pos = copyOld.tableData?.length - 2
            const prevProduct = copyOld.tableData?.[copyOld.tableData?.length - 3]
            const myProduct = copyOld.tableData[pos]
            const newAbsoluteIndex = prevProduct?.absoluteIndex  !== undefined ? parseInt(prevProduct.absoluteIndex) + 1 : 0
            const newOrder = prevProduct?.order  !== undefined ? parseInt(prevProduct.order) + 1 : 1
            const parsedMargin = parseFloat(String(product.margin || 0))
            const parsedCost = parseFloat(String(product.costPrice || 0))
            const rowPriceCost = roundNumberAndDecimals(1 * parsedCost, totalCalculateParams)
            const unitPriceCostWithMargin = roundNumberAndDecimals(parsedCost + (parsedMargin / 100 * parsedCost), totalCalculateParams)
            const rowPriceCostWithMargin = roundNumberAndDecimals(rowPriceCost + (parsedMargin / 100 * rowPriceCost), totalCalculateParams)
            copyOld.tableData[pos] = {
                ...product,
                margin: product.margin || 0,
                cost: product.costPrice || 0,
                ProductId: product.id,
                id: myProduct.id,
                order: newOrder,
                absoluteIndex: newAbsoluteIndex,
                scaleQuantity: 1,
                rowPriceCost,
                unitPriceCostWithMargin,
                rowPriceCostWithMargin
            }
            copyOld.dictPosTableData[myProduct.id] = newAbsoluteIndex
            return copyOld
        })
    }, [setCompleteData])

    const myOnChange: ITLayoutTableOnChangeInputs = useCallback((rowIndex, keyValue, value) => {
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            if (copyOld.tableData?.[rowIndex]) {
                copyOld.tableData[rowIndex][keyValue] = value
            }
            if (keyValue === 'rowPriceCostWithMargin') {
                const newRowPriceCostWithMargin = parseFloat(value)
                copyOld.tableData[rowIndex].rowPriceCostWithMargin = roundNumberAndDecimals(newRowPriceCostWithMargin, totalCalculateParams)
                const parsedRowPriceCost = parseFloat(String(copyOld.tableData[rowIndex].rowPriceCost))
                copyOld.tableData[rowIndex].margin = roundNumberAndDecimals(((newRowPriceCostWithMargin * 100) / parsedRowPriceCost)-100, totalCalculateParams)
            }
            if (keyValue === 'unitPriceCostWithMargin') {
                const newUnitPriceCostWithMargin = parseFloat(value)
                copyOld.tableData[rowIndex].unitPriceCostWithMargin = roundNumberAndDecimals(newUnitPriceCostWithMargin, totalCalculateParams)
                const parsedCost = parseFloat(String(copyOld.tableData[rowIndex].cost))
                copyOld.tableData[rowIndex].margin = roundNumberAndDecimals(((newUnitPriceCostWithMargin * 100) / parsedCost) - 100, totalCalculateParams)
            }
            if (keyValue === 'margin' || keyValue === 'cost' || keyValue === 'scaleQuantity' || keyValue === 'rowPriceCostWithMargin' || keyValue === 'unitPriceCostWithMargin'){
                const parsedMargin = parseFloat(copyOld.tableData[rowIndex].margin || '0')
                const parsedCost = parseFloat(copyOld.tableData[rowIndex].cost || '0')
                const parsedQuantity = parseFloat(copyOld.tableData[rowIndex].scaleQuantity || '0')
                copyOld.tableData[rowIndex].rowPriceCost = roundNumberAndDecimals(parsedQuantity * parsedCost, totalCalculateParams)
                copyOld.tableData[rowIndex].unitPriceCostWithMargin = roundNumberAndDecimals(parsedCost + (parsedMargin/100 * parsedCost), totalCalculateParams)
                copyOld.tableData[rowIndex].rowPriceCostWithMargin = roundNumberAndDecimals(copyOld.tableData[rowIndex].rowPriceCost + (parsedMargin / 100 * copyOld.tableData[rowIndex].rowPriceCost), totalCalculateParams)
            }
            return copyOld
        })
    }, [setCompleteData])

    const deleteLine = useCallback((rowIndex: number) => {
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            const product = copyOld.tableData[rowIndex]
            delete copyOld.selectedProducts[product.parsedId]
            copyOld.tableData.splice(rowIndex, 1)
            copyOld.dictPosTableData = {}
            copyOld.selectedIds = []
            for (const key in copyOld.tableData) {
                const element = copyOld.tableData[key]
                if(element.ProductId){
                    const myParsedKey = parseInt(key)
                    element.absoluteIndex = myParsedKey
                    copyOld.dictPosTableData[element.id] = myParsedKey
                    copyOld.selectedIds.push(element.id)
                }
            }
            return copyOld
        })
    }, [setCompleteData])

    const parseColumns = useCallback((columns: ColumnITLayoutTableConfiguration[]) => {
        let result = addCustomRender(columns as any, {
            onChange: myOnChange,
            onChangeAdd: myOnChangeAdd,
            onChangeFetch: myOnChange,
            onChangeTotals: myOnChange,
            onChangeTotalsFetch: myOnChange
        })
        return result
    }, [addCustomRender, myOnChange, myOnChangeAdd])

    const columns = props.columns
    const parsedColumns = useMemo(() => parseColumns([...columns, {
        id: 'delete',
        dataKey: 'delete',
        label: '',
        simpleLabel: deleteMsg,
        customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteLine(allRowData.absoluteIndex || allRowData.rowIndex)} />,
        width: 30
    }]), [columns, parseColumns, deleteMsg, deleteLine])

    return { ...rest, parsedColumns, eventFunctions, addCustomRender, changeSingleField, onChangeSearchBar, containerRenderer }
}


export const productEscandallingLayoutComponents: DictionaryComponents = {
    ITInputPercentage: ProductEscandallingInputPercentage,
    ProductEscandallingSearchProductInputName: ProductEscandallingSearchProductInputName,
    ITInputQuantity: ProductEscandallingInputQuantity,
    ITDisplayPrice: ITDisplayPrice,
    ITDisplayEditFinalPrice: ITDisplayEditFinalPriceProdEscandalling,
    ITInputPrice: ProductEscandallingInputPrice,
    ITDragHandle: ITDragHandle
}


export default ModalManageProductEscandalling